/**
 * What a call writes into its thread, and only that: the call before it
 * leaves its transcript and its divider in the voice until the new call's
 * start resets them, and a begin on another thread names the new thread
 * before that reset lands. `CallLeftovers` is what the voice held at the
 * begin; none of it is the new call's.
 */
import { NEW_LIVE_TRANSCRIPT, type LiveTranscript } from '@alluzo/shared';
import type { UIMessage } from 'ai';

import type { LiveDividerMark } from '~/features/voice';

import { liveMessageOf } from './live-thread';

/** The transcript and divider the voice held when a begin came: the call before's. */
export type CallLeftovers = { readonly transcript: LiveTranscript; readonly divider: LiveDividerMark | null };

/** The live transcript of the call that is up now: the one the call before left reads as empty. */
export const callTranscriptOf = (transcript: LiveTranscript, left: CallLeftovers | null): LiveTranscript =>
  left !== null && transcript === left.transcript ? NEW_LIVE_TRANSCRIPT : transcript;

/** The divider the call that is up now left, or null: the one the call before left is not its. */
export const callDividerOf = (divider: LiveDividerMark | null, left: CallLeftovers | null): LiveDividerMark | null =>
  left !== null && divider === left.divider ? null : divider;

/** Every finished line of this call's transcript, as the thread keeps it (C10). */
export const finishedLinesOf = (transcript: LiveTranscript, left: CallLeftovers | null): readonly UIMessage[] =>
  callTranscriptOf(transcript, left).rows.flatMap((row) => {
    const message = liveMessageOf(row);
    return message === null ? [] : [message as UIMessage];
  });

/**
 * A spoken line was committed in this box's call: a kept turn, or the
 * person's settled line in the call that is up now. The call before's line
 * (an owed turn after Stop live never reaches the transcript, so its line
 * stays settled there) does not name a fresh thread.
 */
export const spokenCommittedOf = (input: {
  readonly kept: number;
  readonly mine: boolean;
  readonly transcript: LiveTranscript;
  readonly left: CallLeftovers | null;
}): boolean =>
  input.kept > 0 || (input.mine && callTranscriptOf(input.transcript, input.left).rows.some((row) => row.role === 'you' && !row.partial));
